import React from 'react';
import { Target, Users, Award, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Mission = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-blue-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <Target className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-6" />
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">Purpose & Mission</h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto">
            Empowering minorities and young entrepreneurs through mentorship, resources, and networking.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Our Mission
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                <strong className="text-gray-900">Lions Unleashed</strong> is a nonprofit organization established in 2019, striving to provide extensive resources, mentorship, and networking opportunities to minorities and young entrepreneurs. By emphasizing values of professionalism and entrepreneurship, we hope to kickstart the careers of our mentees and prepare them for future endeavors in the corporate world.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                In today's society, we often find that young immigrants and minorities have difficulty making their dreams come true because they lack the network or financial means. This is where we can help! Our team of extremely talented and successful mentors, also known as Lions, have started their own businesses or climbed the ladder through their companies and are ready to guide Cubs through their career choices.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Cubs who apply to this program will also have access to a variety of free resources ranging from workshops, job opportunities, and scholarships.
              </p>
              <p className="text-lg font-semibold text-cyan-600">
                As a Cub, we encourage you to take advantage of this FREE opportunity and join our pride today!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-cyan-500" />
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">Mentorship</h3>
                <p className="text-gray-700">
                  Connecting talented young professionals with experienced industry leaders who provide guidance, support, and real-world insights.
                </p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <Award className="w-12 h-12 mx-auto mb-4 text-blue-500" />
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Excellence</h3>
                <p className="text-gray-700">
                  Fostering a culture of professionalism and entrepreneurship that prepares Cubs for success in the corporate world.
                </p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-purple-500" />
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Opportunity</h3>
                <p className="text-gray-700">
                  Providing access to workshops, networking events, job opportunities, and scholarships to maximize potential.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Our Impact
            </h2>
            <p className="text-base md:text-lg text-gray-700 mb-12">
              Since our founding in 2019, we've made a significant impact on the lives of young professionals:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              <div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-600 mb-2">500+</div>
                <div className="text-sm md:text-base text-gray-700">Cubs Mentored</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-blue-600 mb-2">50+</div>
                <div className="text-sm md:text-base text-gray-700">Expert Lions</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-purple-600 mb-2">100+</div>
                <div className="text-sm md:text-base text-gray-700">Workshops</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-cyan-600 mb-2">$100K+</div>
                <div className="text-sm md:text-base text-gray-700">Scholarships</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliation Section - World Family Organization */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                Member Organization
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center bg-white rounded-3xl shadow-xl p-8 md:p-12">
              <div className="flex justify-center">
                <a href="http://worldfamilyorganization.org/" target="_blank" rel="noopener noreferrer" className="group">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pixel-perfect-132/artifacts/ch4gfwp1_WFO-Logo-Vertical%20%281%29.png" 
                    alt="World Family Organization Logo"
                    className="h-64 md:h-80 object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </a>
              </div>
              
              <div className="space-y-6">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  World Family Organization
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  Lions Unleashed is a proud member of the World Family Organization (WFO), an International Organization dedicated to representing families worldwide, defending their interests, and acting on their behalf with governments and the international community.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  The WFO serves as a vital link between family aspirations and achievements and the work done at the United Nations level, ensuring families have a voice in global policy discussions.
                </p>
                <a 
                  href="http://worldfamilyorganization.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block mt-4 text-cyan-600 hover:text-cyan-700 font-semibold text-base md:text-lg transition-colors"
                >
                  Visit WFO Website →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mission;